import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type SessionInfo } from "@zentrani/api-client";
import { nativeHooks } from "../lib/nativeBridge";

type SessionContextValue = {
  session: SessionInfo | null;
  loading: boolean;
  refresh: () => Promise<SessionInfo | null>;
  /** Apply session from login (or clear) without an extra /session round-trip. */
  apply: (session: SessionInfo | null) => void;
  clear: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  function apply(next: SessionInfo | null) {
    if (next) nativeHooks.onSessionLoaded?.(next);
    setSession(next);
    setLoading(false);
  }

  async function refresh() {
    try {
      const s = await api.auth.session();
      nativeHooks.onSessionLoaded?.(s);
      setSession(s);
      return s;
    } catch (err) {
      // Native app: a rejected Bearer token is forgotten (offline errors keep it).
      if (err instanceof ApiError && err.status === 401) nativeHooks.onSessionCleared?.();
      // Native app offline: open with the last known profile (e.g. frequent visitor QR).
      const offline =
        err instanceof ApiError ? null : ((await nativeHooks.offlineSession?.()) ?? null);
      setSession(offline);
      return offline;
    } finally {
      setLoading(false);
    }
  }

  function clear() {
    nativeHooks.onSessionCleared?.();
    setSession(null);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  // Native app: the API rejected this device's session (see src/native/sessionLost.ts).
  // The event is never dispatched on the web.
  useEffect(() => {
    const onLost = () => {
      setSession(null);
      setLoading(false);
    };
    window.addEventListener("zentrani:session-lost", onLost);
    return () => window.removeEventListener("zentrani:session-lost", onLost);
  }, []);

  return (
    <SessionContext.Provider value={{ session, loading, refresh, apply, clear }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return ctx;
}
